// src/contexts/AudioContext.jsx
"use client"

import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react"

// Import audio files directly - make sure these paths are correct
import flipSound from "../sounds/card-flip.mp3"
import matchSound from "../sounds/right-card.mp3"
import wrongMatchSound from "../sounds/wrong-card.mp3"
import winSound from "../sounds/game-win.mp3"
import loseSound from "../sounds/game-lost.mp3"

export const AudioContext = createContext({
  isMuted: false,
  toggleSound: () => {},
  playSound: () => {},
  isMobileDevice: () => false,
})

export function AudioProvider({ children }) {
  // Check if device is mobile - moved to the top so it's available immediately
  const isMobileDevice = useCallback(() => {
    return (
      typeof window !== "undefined" &&
      (window.innerWidth <= 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent))
    )
  }, [])

  // Initialize muted state - default to true for mobile
  const [isMuted, setIsMuted] = useState(() => {
    // Try to get from localStorage first
    if (typeof window !== "undefined") {
      const savedMuteState = localStorage.getItem("isMuted")
      if (savedMuteState !== null) {
        return savedMuteState === "true"
      }
    }
    // Default to muted on mobile
    return isMobileDevice()
  })

  const audioElementsRef = useRef({})
  const [audioInitialized, setAudioInitialized] = useState(false)

  // Initialize audio elements immediately
  useEffect(() => {
    try {
      // Create audio elements
      const flipAudio = new Audio(flipSound)
      const matchAudio = new Audio(matchSound)
      const wrongMatchAudio = new Audio(wrongMatchSound)
      const winAudio = new Audio(winSound)
      const loseAudio = new Audio(loseSound)

      // Store in ref for immediate access
      audioElementsRef.current = {
        flipAudio,
        matchAudio,
        wrongMatchAudio,
        winAudio,
        loseAudio,
      }

      // Set initial volume based on mute state
      Object.values(audioElementsRef.current).forEach((audio) => {
        audio.volume = isMuted ? 0 : 1
      })

      // Preload audio files without playing them
      Object.values(audioElementsRef.current).forEach((audio) => {
        audio.preload = "auto"
        audio.load()
      })

      setAudioInitialized(true)
      if (process.env.NODE_ENV === "development") console.log("Audio elements initialized successfully, muted:", isMuted)

      // Save initial mute state to localStorage
      localStorage.setItem("isMuted", isMuted.toString())
    } catch (error) {
      console.error("Error initializing audio:", error)
    }
  }, [isMuted]) // Depend on isMuted to ensure correct initial volume

  // Update volumes when mute state changes
  useEffect(() => {
    Object.values(audioElementsRef.current).forEach((audio) => {
      if (audio) {
        audio.volume = isMuted ? 0 : 1
      }
    })
  }, [isMuted])

  // Prime audio on iOS devices - but only if not muted
  const primeAudio = useCallback(() => {
    if (!audioInitialized || isMuted) return

    if (process.env.NODE_ENV === "development") console.log("Priming audio, muted state:", isMuted)

    Object.values(audioElementsRef.current).forEach((audio) => {
      if (audio) {
        // Set volume to 0 temporarily for silent priming
        const originalVolume = audio.volume
        audio.volume = 0

        const playPromise = audio.play()
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              audio.pause()
              audio.currentTime = 0
              // Restore original volume
              audio.volume = originalVolume
            })
            .catch((e) => {
              // Restore original volume even on error
              audio.volume = originalVolume
              if (process.env.NODE_ENV === "development") console.log("Audio priming failed, this is normal on first load:", e)
            })
        }
      }
    })
  }, [audioInitialized, isMuted])

  // Add a click listener to the document to prime audio
  useEffect(() => {
    const handleFirstInteraction = () => {
      primeAudio()
      document.removeEventListener("click", handleFirstInteraction)
    }

    document.addEventListener("click", handleFirstInteraction)

    return () => {
      document.removeEventListener("click", handleFirstInteraction)
    }
  }, [primeAudio])

  const toggleSound = useCallback(() => {
    const newMuteState = !isMuted
    setIsMuted(newMuteState)
    localStorage.setItem("isMuted", newMuteState.toString())

    // Prime audio when unmuting
    if (isMuted && !isMobileDevice()) {
      primeAudio()
    }
  }, [isMuted, primeAudio, isMobileDevice])

  const playSound = useCallback(
    (soundName) => {
      // Skip if muted or on mobile
      if (isMuted || isMobileDevice()) {
        if (process.env.NODE_ENV === "development") console.log(`Skipping sound ${soundName}, muted: ${isMuted}, mobile: ${isMobileDevice()}`)
        return
      }

      // Map sound names to audio elements
      const soundMap = {
        flip: "flipAudio",
        match: "matchAudio",
        wrongMatch: "wrongMatchAudio",
        win: "winAudio",
        lose: "loseAudio",
      }

      const audioKey = soundMap[soundName]
      if (!audioKey) {
        console.warn(`Unknown sound: ${soundName}`)
        return
      }

      const audio = audioElementsRef.current[audioKey]
      if (!audio) {
        console.warn(`Audio element for ${soundName} not found`)
        return
      }

      try {
        // Reset to start
        audio.currentTime = 0

        // Log before playing
        if (process.env.NODE_ENV === "development") console.log(`Playing sound: ${soundName}`)

        // Play with error handling
        const playPromise = audio.play()

        if (playPromise !== undefined) {
          playPromise.catch((e) => {
            console.error(`Error playing ${soundName}:`, e)

            // If autoplay was prevented, try to prime audio again
            if (e.name === "NotAllowedError" && !isMobileDevice()) {
              primeAudio()
            }
          })
        }
      } catch (e) {
        console.error(`Error with ${soundName} audio:`, e)
      }
    },
    [isMuted, isMobileDevice, primeAudio],
  )

  return (
    <AudioContext.Provider
      value={{
        isMuted,
        toggleSound,
        playSound,
        isMobileDevice,
      }}
    >
      {children}
    </AudioContext.Provider>
  )
}

export function useAudio() {
  return useContext(AudioContext)
}

